import { AnyAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useReducer,
} from "react";
import { IFilterFields, ITableRes, initParams } from "./config";
import { Ipage } from "../../../api/types";

interface InitialState {
  createRequest?: ITableRes;
  updateExchangeRate?: ITableRes;
  params: Ipage & Partial<IFilterFields>;
  merchantTabParams: Ipage & Partial<IFilterFields>;
  merchantTabRefetchCounter: number;
  liquidityProviderRefetchCounter: number;
}

const initialState: InitialState = {
  createRequest: undefined,
  updateExchangeRate: undefined,
  params: initParams,
  merchantTabParams: initParams,
  merchantTabRefetchCounter: 0,
  liquidityProviderRefetchCounter: 0,
};

const reduxSlice = createSlice({
  name: "crossMerchantTransferPath",
  initialState,
  reducers: {
    setCreateRequest: (state, action: PayloadAction<any>) => {
      state.createRequest = action.payload;
    },
    setUpdateExchangeRate: (state, action: PayloadAction<any>) => {
      state.updateExchangeRate = action.payload;
    },
    setMerchantTabParams: (
      state,
      action: PayloadAction<Ipage & Partial<IFilterFields>>
    ) => {
      state.merchantTabParams = action.payload;
      state.merchantTabRefetchCounter += 1;
    },

    resetMerchantTabParams: (state) => {
      state.merchantTabParams = initParams;
    },
    setLiquidityProviderParams: (
      state,
      action: PayloadAction<Ipage & Partial<IFilterFields>>
    ) => {
      state.params = action.payload;
      state.liquidityProviderRefetchCounter += 1;
    },

    resetLiquidityProviderParams: (state) => {
      state.params = initParams;
    },
  },
});

const Context = createContext<[InitialState, Dispatch<AnyAction>]>([
  initialState,
  () => {},
]);

const ReduxProvider = ({ children }: { children: ReactNode }) => {
  const { reducer } = reduxSlice;
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <Context.Provider value={[state, dispatch]}>{children}</Context.Provider>
  );
};

const useReduxContext = () => {
  return useContext(Context);
};

const {
  setCreateRequest,
  setLiquidityProviderParams,
  setUpdateExchangeRate,
  resetLiquidityProviderParams,
  setMerchantTabParams,
  resetMerchantTabParams,
} = reduxSlice.actions;

export {
  setCreateRequest,
  setLiquidityProviderParams,
  setUpdateExchangeRate,
  resetLiquidityProviderParams,
  setMerchantTabParams,
  resetMerchantTabParams,
  ReduxProvider,
  useReduxContext,
};
